import React, { useState, useEffect, useCallback } from 'react';
import { ArrowLeft, Building2, CreditCard, Users, Bell, Shield, ArrowUpCircle, Clock, CheckCircle2, XCircle, Loader, AlertCircle } from 'lucide-react';
import { companyService } from '../../../../services/companyService';
import { getPlanById } from '../../../../config/subscriptionPlans';
import { getAllUpgradeRequests, approveUpgradeRequest, rejectUpgradeRequest } from '../../../../services/api/upgradeRequestApi';
import CompanyProfileTab from './CompanyProfileTab';
import CompanySubscriptionTab from './CompanySubscriptionTab';
import CompanyUsersTab from './CompanyUsersTab';
import NotificationSettings from '../settings/NotificationSettings';
import SecuritySettings from '../settings/SecuritySettings';

// ─── Shared status badge ──────────────────────────────────────────────────────
const StatusBadge = ({ status }) => {
    const cfg = {
        PENDING:  { Icon: Clock,        cls: 'bg-amber-100 text-amber-800 border-amber-200',    label: 'Pending' },
        APPROVED: { Icon: CheckCircle2, cls: 'bg-emerald-100 text-emerald-800 border-emerald-200', label: 'Approved' },
        REJECTED: { Icon: XCircle,      cls: 'bg-rose-100 text-rose-800 border-rose-200',       label: 'Rejected' },
    }[status] || { Icon: Clock, cls: 'bg-gray-100 text-gray-600 border-gray-200', label: status };
    const { Icon } = cfg;
    return (
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold rounded-full border ${cfg.cls}`}>
            <Icon size={10} /> {cfg.label}
        </span>
    );
};

// ─── Upgrade Requests Tab ─────────────────────────────────────────────────────
const UpgradeRequestsTab = ({ requests, reviewingId, reviewNotes, reviewError, onNotesChange, onApprove, onReject }) => {
    if (!requests.length) {
        return (
            <div className="h-full flex flex-col items-center justify-center p-8 text-center">
                <div className="w-14 h-14 rounded-2xl bg-indigo-50 flex items-center justify-center mb-3">
                    <ArrowUpCircle size={24} className="text-indigo-400" />
                </div>
                <p className="text-sm font-bold text-gray-600">No Upgrade Requests</p>
                <p className="text-xs text-gray-400 mt-1">This company hasn't submitted any plan upgrade requests.</p>
            </div>
        );
    }

    const pending   = requests.filter(r => r.status === 'PENDING');
    const reviewed  = requests.filter(r => r.status !== 'PENDING');

    return (
        <div className="p-5 max-w-2xl">
            <h3 className="text-base font-bold text-gray-800 mb-4 flex items-center gap-2">
                <ArrowUpCircle size={16} className="text-indigo-600" /> Plan Upgrade Requests
            </h3>

            {reviewError && (
                <div className="flex items-center gap-2 bg-rose-50 border border-rose-200 text-rose-800 px-3 py-2 rounded-xl mb-4 text-xs font-medium">
                    <AlertCircle size={13} /> {reviewError}
                </div>
            )}

            {/* Pending section */}
            {pending.length > 0 && (
                <div className="mb-5">
                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2">Pending Review</p>
                    <div className="space-y-3">
                        {pending.map(r => (
                            <div key={r.publicId} className="bg-amber-50 border border-amber-200 rounded-2xl p-4">
                                <div className="flex items-start justify-between gap-2 mb-2">
                                    <div>
                                        <p className="text-sm font-bold text-gray-900">
                                            {r.currentPlan} → <span className="text-indigo-700">{r.requestedPlan}</span>
                                        </p>
                                        <p className="text-[11px] text-gray-500 mt-0.5">
                                            By {r.requestedByName} · {new Date(r.createdAt).toLocaleDateString('en-AE', { month: 'short', day: 'numeric', year: 'numeric' })}
                                        </p>
                                        {r.requesterNotes && (
                                            <p className="text-xs text-gray-600 italic mt-1">"{r.requesterNotes}"</p>
                                        )}
                                    </div>
                                    <StatusBadge status={r.status} />
                                </div>
                                <div className="mt-3">
                                    <textarea
                                        rows={2}
                                        value={reviewNotes}
                                        onChange={e => onNotesChange(e.target.value)}
                                        placeholder="Admin notes (optional)…"
                                        className="w-full px-3 py-1.5 text-xs border border-amber-300 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-amber-400 resize-none mb-2.5"
                                    />
                                    <div className="flex gap-2">
                                        <button
                                            onClick={() => onApprove(r.publicId)}
                                            disabled={reviewingId === r.publicId}
                                            className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl text-xs font-bold text-white bg-emerald-600 hover:bg-emerald-700 transition-all disabled:opacity-60"
                                        >
                                            {reviewingId === r.publicId ? <Loader size={12} className="animate-spin" /> : <CheckCircle2 size={12} />}
                                            Approve
                                        </button>
                                        <button
                                            onClick={() => onReject(r.publicId)}
                                            disabled={reviewingId === r.publicId}
                                            className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl text-xs font-bold text-white bg-rose-600 hover:bg-rose-700 transition-all disabled:opacity-60"
                                        >
                                            {reviewingId === r.publicId ? <Loader size={12} className="animate-spin" /> : <XCircle size={12} />}
                                            Reject
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* History section */}
            {reviewed.length > 0 && (
                <div>
                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2">History</p>
                    <div className="space-y-2">
                        {reviewed.map(r => (
                            <div key={r.publicId} className="bg-white border border-gray-200 rounded-xl p-3 flex items-center justify-between gap-2">
                                <div>
                                    <p className="text-xs font-bold text-gray-800">{r.currentPlan} → {r.requestedPlan}</p>
                                    <p className="text-[10px] text-gray-400 mt-0.5">
                                        {new Date(r.createdAt).toLocaleDateString('en-AE', { month: 'short', day: 'numeric', year: 'numeric' })}
                                        {r.adminNotes && <> · "{r.adminNotes}"</>}
                                    </p>
                                </div>
                                <StatusBadge status={r.status} />
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};



const CompanyDetailView = ({ companyId, onBack }) => {
    const [company, setCompany] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeSection, setActiveSection] = useState('profile');
    const [upgradeRequests, setUpgradeRequests] = useState([]);
    const [pendingUpgradeCount, setPendingUpgradeCount] = useState(0);
    const [reviewingId, setReviewingId] = useState(null); // publicId being reviewed
    const [reviewNotes, setReviewNotes] = useState('');
    const [reviewError, setReviewError] = useState(null);

    useEffect(() => { loadCompany(); }, [companyId]);

    const loadCompany = async () => {
        try {
            setLoading(true);
            const data = await companyService.getCompanyById(companyId);
            setCompany(data);
            // Load upgrade requests for this specific company
            if (data?.publicId) {
                loadUpgradeRequests(data.publicId);
            }
        } catch (error) {
            console.error('Failed to load company:', error);
        } finally {
            setLoading(false);
        }
    };

    const loadUpgradeRequests = async (tenantPublicId) => {
        try {
            // Load ALL requests then filter by this tenant
            const all = await getAllUpgradeRequests(null).catch(() => []);
            const mine = all.filter(r => r.tenantPublicId === tenantPublicId);
            setUpgradeRequests(mine);
            setPendingUpgradeCount(mine.filter(r => r.status === 'PENDING').length);
        } catch (e) {
            console.warn('Could not load upgrade requests:', e);
        }
    };

    const handleApprove = async (reqPublicId) => {
        try {
            setReviewingId(reqPublicId);
            setReviewError(null);
            await approveUpgradeRequest(reqPublicId, reviewNotes);
            setReviewNotes('');
            await loadCompany();
        } catch (e) {
            setReviewError(e?.response?.data?.message || e.message || 'Approve failed.');
        } finally {
            setReviewingId(null);
        }
    };

    const handleReject = async (reqPublicId) => {
        try {
            setReviewingId(reqPublicId);
            setReviewError(null);
            await rejectUpgradeRequest(reqPublicId, reviewNotes);
            setReviewNotes('');
            await loadCompany();
        } catch (e) {
            setReviewError(e?.response?.data?.message || e.message || 'Reject failed.');
        } finally {
            setReviewingId(null);
        }
    };

    const menuItems = [
        { id: 'profile', label: 'Company Profile', icon: Building2, color: 'text-blue-600', bgColor: 'bg-blue-50' },
        { id: 'subscription', label: 'Subscription', icon: CreditCard, color: 'text-green-600', bgColor: 'bg-green-50' },
        { id: 'users', label: 'Users', icon: Users, color: 'text-purple-600', bgColor: 'bg-purple-50' },
        { id: 'notifications', label: 'Notifications', icon: Bell, color: 'text-orange-600', bgColor: 'bg-orange-50' },
        { id: 'security', label: 'Security', icon: Shield, color: 'text-red-600', bgColor: 'bg-red-50' },
        { id: 'upgrade-requests', label: 'Plan Requests', icon: ArrowUpCircle, color: 'text-indigo-600', bgColor: 'bg-indigo-50', badge: pendingUpgradeCount },
    ];

    const renderContent = () => {
        switch (activeSection) {
            case 'profile':
                return <CompanyProfileTab company={company} onUpdate={loadCompany} />;
            case 'subscription':
                return <CompanySubscriptionTab company={company} onUpdate={loadCompany} />;
            case 'users':
                return <CompanyUsersTab company={company} />;
            case 'notifications':
                return <NotificationSettings />;
            case 'security':
                return <SecuritySettings />;
            case 'upgrade-requests':
                return <UpgradeRequestsTab
                    requests={upgradeRequests}
                    reviewingId={reviewingId}
                    reviewNotes={reviewNotes}
                    reviewError={reviewError}
                    onNotesChange={setReviewNotes}
                    onApprove={handleApprove}
                    onReject={handleReject}
                />;
            default:
                return <CompanyProfileTab company={company} onUpdate={loadCompany} />;
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-screen">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                    <p className="text-gray-600">Loading company details...</p>
                </div>
            </div>
        );
    }

    if (!company) {
        return (
            <div className="flex items-center justify-center h-screen">
                <div className="text-center">
                    <Building2 size={48} className="mx-auto text-gray-300 mb-3" />
                    <h3 className="text-lg font-semibold text-gray-600 mb-1">Company not found</h3>
                    <button
                        onClick={onBack}
                        className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all"
                    >
                        Back to Companies
                    </button>
                </div>
            </div>
        );
    }

    const plan = getPlanById(company.subscriptionPlan);

    return (
        <div className="flex h-screen bg-gradient-to-br from-purple-50 via-blue-50 to-white overflow-hidden">
            {/* Sidebar */}
            <div className="w-64 bg-gradient-to-b from-purple-100/50 to-blue-100/50 backdrop-blur-sm border-r border-purple-200/30 flex flex-col">
                {/* Header */}
                <div className="p-6 border-b border-purple-200/30">
                    <button
                        onClick={onBack}
                        className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 mb-4 transition-colors"
                    >
                        <ArrowLeft size={16} />
                        Back to Companies
                    </button>
                    <div className="flex items-center gap-3 mb-2">
                        <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-500 rounded-xl flex items-center justify-center">
                            <Building2 size={24} className="text-white" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <h1 className="text-lg font-bold bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent truncate">
                                {company.name}
                            </h1>
                            <p className="text-xs text-gray-500 truncate">{company.tradeLicenseNo}</p>
                        </div>
                    </div>
                    <div className="mt-3">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold ${plan?.color === 'blue' ? 'bg-blue-100 text-blue-700' :
                            plan?.color === 'purple' ? 'bg-purple-100 text-purple-700' :
                                plan?.color === 'green' ? 'bg-green-100 text-green-700' :
                                    'bg-gray-100 text-gray-700'
                            }`}>
                            {plan?.name || company.subscriptionPlan} Plan
                        </span>
                    </div>
                </div>

                {/* Menu Items */}
                <nav className="flex-1 p-4 space-y-2 overflow-y-auto custom-scrollbar">
                    {menuItems.map((item) => {
                        const Icon = item.icon;
                        const isActive = activeSection === item.id;
                        return (
                            <button
                                key={item.id}
                                onClick={() => setActiveSection(item.id)}
                                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 ${isActive
                                    ? `${item.bgColor} ${item.color} shadow-md scale-[1.02]`
                                    : 'text-gray-600 hover:bg-white/50 hover:shadow-sm'
                                    }`}
                            >
                                <Icon size={20} className={isActive ? item.color : 'text-gray-400'} />
                                <span className={`text-sm font-semibold flex-1 text-left ${isActive ? '' : 'font-medium'}`}>
                                    {item.label}
                                </span>
                                {item.badge > 0 && (
                                    <span className="text-[10px] font-black bg-rose-500 text-white w-4 h-4 rounded-full flex items-center justify-center shrink-0">
                                        {item.badge}
                                    </span>
                                )}
                            </button>
                        );
                    })}
                </nav>
            </div>

            {/* Main Content */}
            <div className="flex-1 overflow-y-auto custom-scrollbar">
                {renderContent()}
            </div>
        </div>
    );
};

export default CompanyDetailView;
